import { useCallback, useRef, useState } from "react";
import { useWebRTCAudioManager } from "../components/WebRTCAudioManager";
import { useWebSocketAudioManager } from "../components/WebSocketAudioManager";
import { FilterConfig } from "../../../utils/telephonyFilters";

type TransportType = "webrtc" | "websocket";

interface TransportManagerProps {
  onAudioReceived?: (audioData: ArrayBuffer) => void;
  onStreamEvent?: (eventData: any) => void;
  playbackStream?: MediaStream | null;
  isListening?: boolean;
  isThinking?: boolean;
  isSpeaking?: boolean;
  onConnectError?: () => void;
  onConnectionStart?: () => void;
  onConnectionComplete?: () => void;
  preferredTransport?: TransportType;
  language?: string;
  useCase?: string;
  /** "ai" = v1 (STT→LLM), "portal" = v2 (human operator) */
  mode?: "ai" | "portal";
  /** Phone-simulation filter chain (Settings drawer). */
  filterConfig?: FilterConfig | null;
  /** Mic uplink sample rate (8000 = telephony narrowband, 16000 = wideband). */
  inputSampleRate?: number;
  /** Per-call Khmer STT model: "v1" (default) or "v5" (parakeet-khmer-v5). */
  sttModel?: string;
}

export const useTransportManager = ({
  onAudioReceived,
  onStreamEvent,
  playbackStream,
  isListening = false,
  isThinking = false,
  isSpeaking = false,
  onConnectError,
  onConnectionStart,
  onConnectionComplete,
  preferredTransport = "websocket",
  language = "vi",
  useCase = "ha_scenario",
  mode = "ai",
  filterConfig = null,
  inputSampleRate = 16000,
  sttModel = "",
}: TransportManagerProps) => {
  const [transportType, setTransportType] = useState<TransportType>(preferredTransport);
  const [isConnected, setIsConnected] = useState(false);
  const hasTriedWebRTCRef = useRef(false);
  const activeTransportRef = useRef<TransportType | null>(null);

  // WebRTC manager (simplified - no VAD, no custom AEC)
  const webRTCManager = useWebRTCAudioManager({
    onAudioReceived,
    onStreamEvent,
    isListening,
    isThinking,
    isSpeaking,
    onConnectError: () => {
      if (activeTransportRef.current === "webrtc") {
        console.log("❌ [TransportManager] WebRTC connection failed");
      }
    },
    onConnectionStart: () => {
      if (activeTransportRef.current === "webrtc") {
        onConnectionStart?.();
      }
    },
    onConnectionComplete: () => {
      if (activeTransportRef.current === "webrtc") {
        setIsConnected(true);
        onConnectionComplete?.();
      }
    },
    language,
  });

  // WebSocket manager
  const webSocketManager = useWebSocketAudioManager({
    onAudioReceived,
    onStreamEvent,
    playbackStream,
    isListening,
    isThinking,
    isSpeaking,
    onConnectError: () => {
      if (activeTransportRef.current === "websocket") {
        setIsConnected(false);
        onConnectError?.();
      }
    },
    onConnectionStart: () => {
      if (activeTransportRef.current === "websocket") {
        onConnectionStart?.();
      }
    },
    onConnectionComplete: () => {
      if (activeTransportRef.current === "websocket") {
        setIsConnected(true);
        onConnectionComplete?.();
      }
    },
    language,
    useCase,
    mode,
    filterConfig,
    inputSampleRate,
    sttModel,
  });

  // Connect with fallback logic
  const connect = useCallback(async () => {
    console.log("🔌 [TransportManager] ========================================");
    console.log("🔌 [TransportManager] Starting connection...");
    console.log("🔌 [TransportManager] Preferred transport:", preferredTransport);
    console.log("🔌 [TransportManager] ========================================");

    // Try preferred transport first
    if (preferredTransport === "webrtc" && !hasTriedWebRTCRef.current) {
      try {
        console.log("🔌 [TransportManager] Attempting WebRTC connection...");
        activeTransportRef.current = "webrtc";
        setTransportType("webrtc");
        hasTriedWebRTCRef.current = true;
        await webRTCManager.connect();
        console.log("✅ [TransportManager] WebRTC connected successfully!");
        return;
      } catch (error) {
        console.warn("⚠️ [TransportManager] WebRTC FAILED! Error:", error);
        console.warn("⚠️ [TransportManager] Falling back to WebSocket...");
        activeTransportRef.current = null;
      }
    }

    // Fallback to WebSocket
    try {
      console.log("🔌 [TransportManager] Attempting WebSocket connection...");
      activeTransportRef.current = "websocket";
      setTransportType("websocket");
      await webSocketManager.connect();
      console.log("✅ [TransportManager] WebSocket connected successfully");
    } catch (error) {
      console.error("❌ [TransportManager] All transport methods failed:", error);
      activeTransportRef.current = null;
      setIsConnected(false);
      onConnectError?.();
      throw error;
    }
  }, [preferredTransport, webRTCManager, webSocketManager, onConnectError]);

  // Disconnect from active transport
  const disconnect = useCallback(async (reason: string = "client_disconnect") => {
    console.log(`🔌 [TransportManager] Disconnecting from ${activeTransportRef.current}...`);

    if (activeTransportRef.current === "webrtc") {
      await webRTCManager.disconnect();
    } else if (activeTransportRef.current === "websocket") {
      webSocketManager.disconnect(reason);
    }

    activeTransportRef.current = null;
    hasTriedWebRTCRef.current = false;
    setIsConnected(false);
  }, [webRTCManager, webSocketManager]);

  // Send event through active transport
  const sendEvent = useCallback((eventType: string, data?: any) => {
    if (activeTransportRef.current === "webrtc") {
      webRTCManager.sendEvent(eventType, data);
    } else if (activeTransportRef.current === "websocket") {
      webSocketManager.sendEvent(eventType, data);
    }
  }, [webRTCManager, webSocketManager]);

  // Stop audio processing
  const stopAudioProcessing = useCallback(() => {
    if (activeTransportRef.current === "webrtc") {
      webRTCManager.stopAudioProcessing();
    } else if (activeTransportRef.current === "websocket") {
      webSocketManager.stopAudioProcessing();
    }
  }, [webRTCManager, webSocketManager]);

  // Update playback stream
  const updatePlaybackStream = useCallback((stream: MediaStream | null) => {
    if (activeTransportRef.current === "webrtc") {
      webRTCManager.updatePlaybackStream(stream);
    } else if (activeTransportRef.current === "websocket") {
      webSocketManager.updatePlaybackStream(stream);
    }
  }, [webRTCManager, webSocketManager]);

  // Update listening state
  const updateListeningState = useCallback((listening: boolean) => {
    if (activeTransportRef.current === "webrtc") {
      webRTCManager.updateListeningState(listening);
    } else if (activeTransportRef.current === "websocket") {
      webSocketManager.updateListeningState(listening);
    }
  }, [webRTCManager, webSocketManager]);

  // Update thinking state
  const updateThinkingState = useCallback((thinking: boolean) => {
    if (activeTransportRef.current === "webrtc") {
      webRTCManager.updateThinkingState(thinking);
    } else if (activeTransportRef.current === "websocket") {
      webSocketManager.updateThinkingState(thinking);
    }
  }, [webRTCManager, webSocketManager]);

  // Mute audio (WebRTC only)
  const muteAudio = useCallback(() => {
    if (activeTransportRef.current === "webrtc") {
      webRTCManager.muteAudio();
    }
  }, [webRTCManager]);

  // Unmute audio (WebRTC only)
  const unmuteAudio = useCallback(() => {
    if (activeTransportRef.current === "webrtc") {
      webRTCManager.unmuteAudio();
    }
  }, [webRTCManager]);

  // Stop audio (WebRTC only)
  const stopAudio = useCallback(() => {
    if (activeTransportRef.current === "webrtc") {
      webRTCManager.stopAudio();
    }
  }, [webRTCManager]);

  // Get client ID from active transport
  const clientId = activeTransportRef.current === "webrtc"
    ? webRTCManager.clientId
    : webSocketManager.clientId;

  return {
    connect,
    disconnect,
    sendEvent,
    stopAudioProcessing,
    updatePlaybackStream,
    updateListeningState,
    updateThinkingState,
    muteAudio,
    unmuteAudio,
    stopAudio,
    isConnected,
    clientId,
    transportType,
    isUserSpeaking: false, // VAD removed, backend handles this
  };
};
